import {
  SOCCER_H,
  SOCCER_W,
  soccerVariantConfig,
  type SoccerTeam,
  type SoccerVariantId,
} from "../../../lib/multiplayer/soccer-sim";

export type SoccerViewMode = "pc" | "mobile" | "local-pc" | "local-mobile";

export type SoccerView = {
  mode: SoccerViewMode;
  localTeam: SoccerTeam;
  playX: number;
  playY: number;
  playW: number;
  playH: number;
  worldW: number;
  worldH: number;
  variant: SoccerVariantId;
};

/** Vertical phone layout only — tablets use horizontal PC layout. */
export function isSoccerMobileView(): boolean {
  if (typeof window === "undefined" || typeof matchMedia === "undefined") return false;
  if (!matchMedia("(pointer: coarse)").matches) return false;
  const minDim = Math.min(window.innerWidth, window.innerHeight);
  return minDim < 640;
}

/** Touch / stylus / iPad: use on-screen BOOST toggle (not left-click hint). */
export function isSoccerTouchBoostUi(): boolean {
  if (typeof window === "undefined" || typeof matchMedia === "undefined") return false;
  return matchMedia("(pointer: coarse)").matches || matchMedia("(hover: none)").matches;
}

export function computeSoccerView(
  canvasW: number,
  canvasH: number,
  localTeam: SoccerTeam,
  shared: boolean,
  variant: SoccerVariantId = "normal",
): SoccerView {
  const cfg = soccerVariantConfig(variant);
  const mobile = isSoccerMobileView();
  const mode: SoccerViewMode = shared
    ? mobile
      ? "local-mobile"
      : "local-pc"
    : mobile
      ? "mobile"
      : "pc";
  const topHud = Math.max(38, canvasH * 0.052);
  const botHud = Math.max(variant === "mega" ? 56 : 44, canvasH * (variant === "mega" ? 0.078 : 0.062));
  const side = Math.max(6, canvasW * 0.014);
  const availW = Math.max(80, canvasW - side * 2);
  const availH = Math.max(80, canvasH - topHud - botHud);
  const vertical = mode === "mobile" || mode === "local-mobile";
  const aspect = vertical ? cfg.h / cfg.w : cfg.w / cfg.h;
  let playW = availW;
  let playH = playW / aspect;
  if (playH > availH) {
    playH = availH;
    playW = playH * aspect;
  }
  return {
    mode,
    localTeam,
    playX: (canvasW - playW) / 2,
    playY: topHud + (availH - playH) / 2,
    playW,
    playH,
    worldW: cfg.w,
    worldH: cfg.h,
    variant,
  };
}

/** World → view-normalized 0–1 inside the play rect. */
export function worldToViewNorm(x: number, y: number, view: SoccerView): { x: number; y: number } {
  const W = view.worldW || SOCCER_W;
  const H = view.worldH || SOCCER_H;
  let nx = x / W;
  let ny = y / H;
  switch (view.mode) {
    case "local-pc":
      break;
    case "local-mobile":
      return { x: ny, y: 1 - nx };
    case "pc":
      if (view.localTeam === "blue") nx = 1 - nx;
      break;
    case "mobile":
      if (view.localTeam === "blue") return { x: ny, y: 1 - nx };
      return { x: 1 - ny, y: nx };
  }
  return { x: nx, y: ny };
}

export function viewNormToWorld(vx: number, vy: number, view: SoccerView): { x: number; y: number } {
  const W = view.worldW || SOCCER_W;
  const H = view.worldH || SOCCER_H;
  switch (view.mode) {
    case "local-pc":
      return { x: vx * W, y: vy * H };
    case "local-mobile":
      return { x: (1 - vy) * W, y: vx * H };
    case "pc":
      if (view.localTeam === "blue") return { x: (1 - vx) * W, y: vy * H };
      return { x: vx * W, y: vy * H };
    case "mobile":
      if (view.localTeam === "blue") return { x: (1 - vy) * W, y: vx * H };
      return { x: vy * W, y: (1 - vx) * H };
  }
}

export function worldToScreen(x: number, y: number, view: SoccerView): { x: number; y: number } {
  const n = worldToViewNorm(x, y, view);
  return { x: view.playX + n.x * view.playW, y: view.playY + n.y * view.playH };
}

export function screenToWorld(sx: number, sy: number, view: SoccerView): { x: number; y: number } {
  const vx = (sx - view.playX) / Math.max(1, view.playW);
  const vy = (sy - view.playY) / Math.max(1, view.playH);
  return viewNormToWorld(vx, vy, view);
}

export function soccerScale(view: SoccerView): number {
  const W = view.worldW || SOCCER_W;
  const vertical = view.mode === "mobile" || view.mode === "local-mobile";
  return vertical ? view.playH / W : view.playW / W;
}
